import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePackages } from '../../hooks/usePackages';
import { 
  PlusIcon, 
  PencilSquareIcon, 
  TrashIcon, 
  CubeIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import { Package, PREDEFINED_PACKAGES, AVAILABLE_MODULES, PackageType } from '../../types/package';
import { Button } from '../common/Button';
import Modal from '../common/Modal';
import { formatCurrency } from '../../utils/formatters';
import { validatePackageModules, getCoreModules } from '../../utils/moduleValidator';

interface PackageManagerProps {
  packages?: Package[];
  onPackageCreate?: (packageData: Omit<Package, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onPackageUpdate?: (id: string, packageData: Partial<Package>) => void;
  onPackageDelete?: (id: string) => void;
}

const PackageManager: React.FC<PackageManagerProps> = ({
  packages: propPackages = [],
  onPackageCreate,
  onPackageUpdate,
  onPackageDelete
}) => {
  const { t } = useLanguage();
  const { packages: hookPackages, loading, createPackage, updatePackage, deletePackage } = usePackages();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [formData, setFormData] = useState<Omit<Package, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    description: '',
    type: 'basic',
    price: {
      monthly: 0,
      yearly: 0,
      currency: 'HUF'
    },
    maxUsers: 10,
    maxOrganizations: 1,
    maxStorage: 10,
    features: [],
    modules: ['customers', 'settings'],
    isActive: true
  });

  // Használjuk a prop-okat vagy a hook-ból jövő adatokat
  const packages = propPackages.length > 0 ? propPackages : hookPackages;

  const handleCreatePackage = async () => {
    try {
      // Modulok validációja
      const validation = validatePackageModules(formData.modules);
      if (!validation.isValid) {
        alert(`Hibás modulok: ${validation.invalidModules.join(', ')}\nHiányzó core modulok: ${validation.missingCoreModules.join(', ')}`);
        return;
      }

      if (onPackageCreate) {
        onPackageCreate(formData);
      } else {
        await createPackage(formData);
      }
      
      setIsCreateModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error creating package:', error);
    }
  };

  const handleEditPackage = (pkg: Package) => {
    setSelectedPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description,
      type: pkg.type,
      price: pkg.price,
      maxUsers: pkg.maxUsers,
      maxOrganizations: pkg.maxOrganizations,
      maxStorage: pkg.maxStorage,
      features: pkg.features,
      modules: pkg.modules,
      isActive: pkg.isActive,
      isPopular: pkg.isPopular,
      customModules: pkg.customModules
    });
    setIsEditModalOpen(true);
  };

  const handleUpdatePackage = async () => {
    if (!selectedPackage) return;

    try {
      if (onPackageUpdate) {
        onPackageUpdate(selectedPackage.id, formData);
      } else {
        await updatePackage(selectedPackage.id, formData);
      }
      
      setIsEditModalOpen(false);
      setSelectedPackage(null);
      resetForm();
    } catch (error) {
      console.error('Error updating package:', error);
    }
  };

  const handleDeletePackage = async (pkg: Package) => {
    if (window.confirm(`Biztosan törölni szeretné a "${pkg.name}" csomagot?`)) {
      try {
        if (onPackageDelete) {
          onPackageDelete(pkg.id);
        } else {
          await deletePackage(pkg.id);
        }
      } catch (error) {
        console.error('Error deleting package:', error);
      }
    }
  };

  const resetForm = () => {
    const coreModules = getCoreModules();
    setFormData({
      name: '',
      description: '',
      type: 'basic',
      price: {
        monthly: 0,
        yearly: 0,
        currency: 'HUF'
      },
      maxUsers: 10,
      maxOrganizations: 1,
      maxStorage: 10,
      features: [],
      modules: [...coreModules], // Core modulok automatikusan
      isActive: true
    });
  };

  const handleModuleToggle = (moduleId: string) => {
    const isSelected = formData.modules.includes(moduleId);
    if (isSelected) {
      // Core modulokat nem lehet eltávolítani
      const module = AVAILABLE_MODULES.find(m => m.id === moduleId);
      if (module?.isCore) return;
      
      setFormData({
        ...formData,
        modules: formData.modules.filter(id => id !== moduleId)
      });
    } else {
      setFormData({
        ...formData,
        modules: [...formData.modules, moduleId]
      });
    }
  };

  const getPackageTypeColor = (type: PackageType) => {
    switch (type) {
      case 'free': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'basic': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pro': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'enterprise': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'custom': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getModulesByCategory = () => {
    const categories = AVAILABLE_MODULES.reduce((acc, module) => {
      if (!acc[module.category]) {
        acc[module.category] = [];
      }
      acc[module.category].push(module);
      return acc;
    }, {} as Record<string, typeof AVAILABLE_MODULES>);

    return categories;
  };

  const modulesByCategory = getModulesByCategory();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Csomagkezelés</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Előfizetési csomagok és modulok kezelése</p>
        </div>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center space-x-2"
        >
          <PlusIcon className="h-4 w-4" />
          <span>Új csomag</span>
        </Button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Csomagok betöltése...</p>
        </div>
      )}

      {/* Packages Grid */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
          <div
            key={pkg.id}
            className={`bg-white dark:bg-gray-800 rounded-lg border-2 shadow-sm hover:shadow-md transition-shadow ${
              pkg.isPopular ? 'border-purple-200 dark:border-purple-800 ring-2 ring-purple-100 dark:ring-purple-900/50' : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            {/* Package Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPackageTypeColor(pkg.type)}`}>
                  {pkg.type.toUpperCase()}
                </span>
                {pkg.isPopular && (
                  <StarIcon className="h-5 w-5 text-yellow-500 fill-current" />
                )}
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{pkg.name}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">{pkg.description}</p>
              
              <div className="mt-4">
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {formatCurrency(pkg.price.monthly, pkg.price.currency)}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400 ml-1">/hó</span>
                </div>
                {pkg.price.yearly > 0 && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    vagy {formatCurrency(pkg.price.yearly, pkg.price.currency)}/év
                  </div>
                )}
              </div>
            </div>

            {/* Package Details */}
            <div className="p-6 space-y-4">
              {/* Limits */}
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <UserGroupIcon className="h-4 w-4 mr-2" />
                  <span>
                    {pkg.maxUsers === -1 ? 'Korlátlan felhasználó' : `${pkg.maxUsers} felhasználó`}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                  <span>
                    {pkg.maxOrganizations === -1 ? 'Korlátlan szervezet' : `${pkg.maxOrganizations} szervezet`}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                  <span>{pkg.maxStorage} GB tárhely</span>
                </div>
              </div>

              {/* Modules */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Modulok ({pkg.modules.length})</h4>
                <div className="flex flex-wrap gap-1">
                  {pkg.modules.slice(0, 4).map((moduleId) => {
                    const module = AVAILABLE_MODULES.find(m => m.id === moduleId);
                    return module ? (
                      <span
                        key={moduleId}
                        className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      >
                        {module.name}
                      </span>
                    ) : null;
                  })}
                  {pkg.modules.length > 4 && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                      +{pkg.modules.length - 4} további
                    </span>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  {pkg.isActive ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                  )}
                  <span className={`text-sm ${pkg.isActive ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                    {pkg.isActive ? 'Aktív' : 'Inaktív'}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditPackage(pkg)}
                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeletePackage(pkg)}
                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>
      )}

      {/* Create Package Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Új csomag létrehozása"
        size="4xl"
      >
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Csomag neve
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
                placeholder="pl. Professzionális"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Csomag típusa
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as PackageType })}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              >
                <option value="free">Ingyenes</option>
                <option value="basic">Alap</option>
                <option value="pro">Professzionális</option>
                <option value="enterprise">Vállalati</option>
                <option value="custom">Egyedi</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Leírás
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
              placeholder="Csomag részletes leírása..."
            />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Havi ár (HUF)
              </label>
              <input
                type="number"
                value={formData.price.monthly}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  price: { ...formData.price, monthly: parseInt(e.target.value) || 0 }
                })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Éves ár (HUF)
              </label>
              <input
                type="number"
                value={formData.price.yearly}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  price: { ...formData.price, yearly: parseInt(e.target.value) || 0 }
                })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tárhely (GB)
              </label>
              <input
                type="number"
                value={formData.maxStorage}
                onChange={(e) => setFormData({ ...formData, maxStorage: parseInt(e.target.value) || 0 })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
              />
            </div>
          </div>

          {/* Limits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max. felhasználók (-1 = korlátlan)
              </label>
              <input
                type="number"
                value={formData.maxUsers}
                onChange={(e) => setFormData({ ...formData, maxUsers: parseInt(e.target.value) || 0 })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max. szervezetek (-1 = korlátlan)
              </label>
              <input
                type="number"
                value={formData.maxOrganizations}
                onChange={(e) => setFormData({ ...formData, maxOrganizations: parseInt(e.target.value) || 0 })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
              />
            </div>
          </div>

          {/* Modules Selection */}
          <div>
            <h3 className="text-base font-medium text-gray-900 mb-4">Modulok kiválasztása</h3>
            <div className="space-y-4">
              {Object.entries(modulesByCategory).map(([category, modules]) => {
                const categoryColors = {
                  core: 'from-blue-100 to-indigo-100',
                  crm: 'from-green-100 to-teal-100', 
                  products: 'from-orange-100 to-red-100',
                  sales: 'from-purple-100 to-fuchsia-100',
                  documents: 'from-amber-100 to-orange-100',
                  projects: 'from-indigo-100 to-violet-100',
                  finance: 'from-emerald-100 to-cyan-100',
                  admin: 'from-gray-100 to-slate-100'
                };
                
                return (
                  <div key={category} className="bg-white shadow-sm rounded-lg border border-gray-200">
                    <div className="flex">
                      <div className={`p-3 bg-gradient-to-br ${categoryColors[category as keyof typeof categoryColors] || 'from-gray-100 to-slate-100'} flex items-center justify-center rounded-l-lg border-r border-gray-200`}>
                        <CubeIcon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="p-4 flex-1">
                        <h4 className="text-sm font-medium text-gray-800 mb-3">
                          {category === 'crm' ? 'CRM' : 
                           category === 'products' ? 'Termékek' :
                           category === 'sales' ? 'Értékesítés' :
                           category === 'documents' ? 'Dokumentumok' :
                           category === 'projects' ? 'Projektek' :
                           category === 'finance' ? 'Pénzügy' :
                           category === 'admin' ? 'Adminisztráció' :
                           category === 'core' ? 'Alapfunkciók' : category}
                          <span className="ml-2 text-xs text-gray-500">({modules.length} modul)</span>
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                          {modules.map((module) => (
                            <label
                              key={module.id}
                              className={`flex items-center justify-between p-2 border rounded cursor-pointer transition-colors ${
                                formData.modules.includes(module.id)
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              } ${module.isCore ? 'opacity-75 cursor-not-allowed' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.modules.includes(module.id)}
                                onChange={() => handleModuleToggle(module.id)}
                                disabled={module.isCore}
                                className="sr-only"
                              />
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <span className="text-sm text-gray-900 truncate">
                                  {module.name}
                                  {module.isCore && (
                                    <span className="ml-1 text-xs text-blue-600 font-medium">
                                      (Core)
                                    </span>
                                  )}
                                </span>
                              </div>
                              {formData.modules.includes(module.id) && (
                                <CheckCircleIcon className="h-5 w-5 text-primary-500 flex-shrink-0" />
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">Aktív csomag</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isPopular || false}
                onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">Népszerű csomag</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => setIsCreateModalOpen(false)}
            >
              Mégse
            </Button>
            <Button onClick={handleCreatePackage}>
              Csomag létrehozása
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Package Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Csomag szerkesztése"
        size="4xl"
      >
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Csomag neve
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
                placeholder="pl. Professzionális"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Csomag típusa
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as PackageType })}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              >
                <option value="free">Ingyenes</option>
                <option value="basic">Alap</option>
                <option value="pro">Professzionális</option>
                <option value="enterprise">Vállalati</option>
                <option value="custom">Egyedi</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Leírás
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
              placeholder="Csomag részletes leírása..."
            />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Havi ár (HUF)
              </label>
              <input
                type="number"
                value={formData.price.monthly}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  price: { ...formData.price, monthly: parseInt(e.target.value) || 0 }
                })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Éves ár (HUF)
              </label>
              <input
                type="number"
                value={formData.price.yearly}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  price: { ...formData.price, yearly: parseInt(e.target.value) || 0 }
                })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tárhely (GB)
              </label>
              <input
                type="number"
                value={formData.maxStorage}
                onChange={(e) => setFormData({ ...formData, maxStorage: parseInt(e.target.value) || 0 })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
              />
            </div>
          </div>

          {/* Limits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max felhasználók
              </label>
              <input
                type="number"
                value={formData.maxUsers}
                onChange={(e) => setFormData({ ...formData, maxUsers: parseInt(e.target.value) || 0 })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
                placeholder="-1 = korlátlan"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max szervezetek
              </label>
              <input
                type="number"
                value={formData.maxOrganizations}
                onChange={(e) => setFormData({ ...formData, maxOrganizations: parseInt(e.target.value) || 0 })}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700"
                placeholder="-1 = korlátlan"
              />
            </div>
          </div>

          {/* Modules Selection */}
          <div>
            <h3 className="text-base font-medium text-gray-900 mb-4">Modulok kiválasztása</h3>
            <div className="space-y-4">
              {Object.entries(modulesByCategory).map(([category, modules]) => {
                const categoryColors = {
                  core: 'from-blue-100 to-indigo-100',
                  crm: 'from-green-100 to-teal-100', 
                  products: 'from-orange-100 to-red-100',
                  sales: 'from-purple-100 to-fuchsia-100',
                  documents: 'from-amber-100 to-orange-100',
                  projects: 'from-indigo-100 to-violet-100',
                  finance: 'from-emerald-100 to-cyan-100',
                  admin: 'from-gray-100 to-slate-100'
                };
                
                return (
                  <div key={category} className="bg-white shadow-sm rounded-lg border border-gray-200">
                    <div className="flex">
                      <div className={`p-3 bg-gradient-to-br ${categoryColors[category as keyof typeof categoryColors] || 'from-gray-100 to-slate-100'} flex items-center justify-center rounded-l-lg border-r border-gray-200`}>
                        <CubeIcon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="p-4 flex-1">
                        <h4 className="text-sm font-medium text-gray-800 mb-3">
                          {category === 'crm' ? 'CRM' : 
                           category === 'products' ? 'Termékek' :
                           category === 'sales' ? 'Értékesítés' :
                           category === 'documents' ? 'Dokumentumok' :
                           category === 'projects' ? 'Projektek' :
                           category === 'finance' ? 'Pénzügy' :
                           category === 'admin' ? 'Adminisztráció' :
                           category === 'core' ? 'Alapfunkciók' : category}
                          <span className="ml-2 text-xs text-gray-500">({modules.length} modul)</span>
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                          {modules.map((module) => (
                            <label
                              key={module.id}
                              className={`flex items-center justify-between p-2 border rounded cursor-pointer transition-colors ${
                                formData.modules.includes(module.id)
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              } ${module.isCore ? 'opacity-75 cursor-not-allowed' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.modules.includes(module.id)}
                                onChange={() => handleModuleToggle(module.id)}
                                disabled={module.isCore}
                                className="sr-only"
                              />
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <span className="text-sm text-gray-900 truncate">
                                  {module.name}
                                  {module.isCore && (
                                    <span className="ml-1 text-xs text-blue-600 font-medium">
                                      (Core)
                                    </span>
                                  )}
                                </span>
                              </div>
                              {formData.modules.includes(module.id) && (
                                <CheckCircleIcon className="h-5 w-5 text-primary-500 flex-shrink-0" />
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Settings */}
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">Aktív csomag</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isPopular}
                onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">Népszerű csomag</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
            >
              Mégse
            </Button>
            <Button onClick={handleUpdatePackage}>
              Módosítások mentése
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PackageManager;
